/* eslint-disable react/prop-types */
import { useRouter } from 'next/router';
import axios from 'axios';
import { isCustomPageUri, getPathNameFromUrl, handleRedirectsAndReturnData } from '@/utils/misc';
import { getProductsData } from '@/utils/woocommerce';
import { getPages, getPage } from '@/utils/endpoints';
import HomePage from '@/components/page-templates/HomePage';
import ContentPage from '@/components/page-templates/ContentPage';
import DefaultPageTemplate from '@/components/page-templates/DefaultPageTemplate';
import Loader from '@/components/util/Loader';

const Page = ({ pageData, featuredProducts, sliderProducts, crossSellProducts, sideMenuData }) => {
  const router = useRouter();

  if (router.isFallback) {
    return <Loader />;
  }

  // Map templates to components
  const templates = {
    'beaver-next-home.php': HomePage,
    'beaver-next-content_page.php': ContentPage,
  };

  const PageTemplate = templates[pageData?.template] || DefaultPageTemplate;

  return (
    <PageTemplate
      pageData={pageData}
      featuredProducts={featuredProducts}
      sliderProducts={sliderProducts}
      crossSellProducts={crossSellProducts}
      sideMenuData={sideMenuData}
    />
  );
};

export default Page;

export async function getStaticProps({ params }) {
  const { data } = await axios.get(`${process.env.NEXT_PUBLIC_WORDPRESS_SITE_URL}wp-json/beaver_next_wordpress/v1/settings`);
  const { data: studioData } = await axios.get(`${process.env.NEXT_PUBLIC_WORDPRESS_SITE_URL}wp-json/beaver_next_wordpress/v1/acf_studio`);

  let lang = 'en';

  if (data?.language) {
    lang = data.language.split('_')[0];
  }

  const messages = await import(`../messages/${lang}.json`);
  const pageData = await getPage(params?.slug.pop() ?? '');

  // Fetch side menu data if enabled
  let sideMenuData = null;
  if (pageData?.[0]?.acf?.enable_side_menu && pageData?.[0]?.acf?.select_menu) {
    try {
      const { data: menuData } = await axios.get(
        `${process.env.NEXT_PUBLIC_WORDPRESS_SITE_URL}wp-json/beaver_next_wordpress/v1/menu/${pageData[0].acf.select_menu}`,
      );
      sideMenuData = menuData;
    } catch (error) {
      console.error('Error fetching menu data:', error);
    }
  }

  // Initialize products arrays for different sections
  const featuredProductsList = [];
  const sliderProductsList = [];
  let crossSellProducts = [];

  // Check if sections exist and fetch products accordingly
  if (pageData?.[0]?.acf?.sections) {
    for (const section of pageData[0].acf.sections) {
      if (section.acf_fc_layout === 'Featured_Products') {
        const settings = section.products_source;
        const { data: fetchedProducts, crossSellProducts: fetchedCrossSellProducts } = await getProductsData(
          settings?.per_page,
          'publish',
          settings?.featured,
          false,
          settings?.category,
          1,
          settings?.include,
          settings?.order_by,
          settings?.order,
        );
        featuredProductsList.push(fetchedProducts || {});
        crossSellProducts = [...crossSellProducts, ...(fetchedCrossSellProducts || [])];
      }

      if (section.acf_fc_layout === 'Products_Slider') {
        const settings = section.products_source;
        const { data: fetchedProducts, crossSellProducts: fetchedCrossSellProducts } = await getProductsData(
          settings?.per_page,
          'publish',
          settings?.featured,
          false,
          settings?.category,
          1,
          settings?.include,
          settings?.order_by,
          settings?.order,
        );
        sliderProductsList.push(fetchedProducts || {});
        crossSellProducts = [...crossSellProducts, ...(fetchedCrossSellProducts || [])];
      }
    }
  }

  const defaultProps = {
    props: {
      data: data || {},
      studioData: studioData || {},
      messages: messages.default,
      pageData: pageData?.[0] ?? {},
      featuredProductsList,
      sliderProductsList,
      crossSellProducts,
      sideMenuData: sideMenuData,
    },
    revalidate: 300,
  };

  return handleRedirectsAndReturnData(defaultProps, pageData);
}

export async function getStaticPaths() {
  const pagesData = await getPages();
  const pathsData = [];

  Array.isArray(pagesData) && pagesData.map(page => {
    const pathName = getPathNameFromUrl(page?.link ?? '');

    // Build paths data.
    if (!pathName && !isCustomPageUri(pathName)) {
      const slugs = pathName?.split('/').filter(pageSlug => pageSlug);
      pathsData.push({ params: { slug: slugs } });
    }
  });

  return {
    paths: pathsData,
    fallback: 'blocking',
  };
}
